<template>
	<div style="display: none;">
		<button @click="createInstance">update</button>
	</div>
</template>

<script>
import { createPopper } from '@popperjs/core';
import _ from 'lodash';

export default {
	name: 'PopperPosition',
	data() {
		return {
			trackedRef: () => {},
			popperRef: () => {},
			arrowRef: () => {},
			popperInstance: undefined,
			popperMinWidth: 'auto',
			currentPositionMod: {
				name: 'currentPositionMod',
				enabled: true,
				phase: 'main',
				fn: this.positionModFn
			},
			altBoundayScrollEvent: null,
			pageScrollEvent: null
		};
	},
	props: {
		altBoundary: {
			type: String,
			default: undefined
		},
		trackedRefName: {
			type: [String, HTMLElement],
			default: undefined
		},
		popoverRefName: {
			type: [String, HTMLElement],
			default: undefined
		},
		arrowRefName: {
			type: [String, HTMLElement],
			default: undefined
		},
		placement: {
			type: String,
			default: 'auto'
		},
		popperVisible: {
			type: Boolean,
			default: false,
			required: true
		},
		modifiers: {
			type: Array,
			default: () => []
		},
		trackerWatch: {
			type: [Object, String],
			default: null
		},
		strategy: {
			type: String,
			default: 'absolute'
		},
		offset: {
			type: Array,
			default: () => [0, 0]
		}
	},
	mounted() {
		if (this.popperVisible && this.trackedRefName && this.popoverRefName) {
			this.createInstance();
		}

		if (Object.values(this.offset).findIndex(value => value !== 0) !== -1) {
			this.modifiers.push({
				name: 'offset',
				options: {
					offset: this.offset
				}
			});
		}
	},
	watch: {
		popperVisible(newValue) {
			if (newValue) {
				this.createInstance();
				this.handlePageScrollEvent();
				this.handleAltBoundary();
			} else {
				removeEventListener('scroll', this.pageScrollEvent);
				removeEventListener('scroll', this.altBoundayScrollEvent);
			}
		},
		trackerWatch(newValue) {
			if (newValue) {
				this.createInstance();
			}
		},
		trackedRefName: {
			immediate: false,
			deep: false,
			handler(newValue) {
				if (newValue) {
					this.createInstance();
				}
			}
		},
		popoverRefName: {
			immediate: true,
			deep: false,
			handler(newValue) {
				if (newValue) {
					this.createInstance();
				}
			}
		},
		arrowRefName(newValue) {
			if (newValue) {
				this.createInstance();
			}
		},
		placement(newValue) {
			if (newValue) {
				this.createInstance();
			}
		}
	},
	methods: {
		onUpdate() {
			this.popperInstance.update();
		},
		createInstance() {
			const execInstance = this.execCreateInstance.bind(this);
			const setMinWidth = this.setPopperMinWidth.bind(this);

			this.$nextTick()
				.then(execInstance)
				.then(setMinWidth);
		},
		sanitazeRefs() {
			if (
				this.$parent.$refs[this.trackedRefName] &&
				typeof this.trackedRefName === 'string'
			) {
				if (Array.isArray(this.$parent.$refs[this.trackedRefName])) {
					this.trackedRef = this.$parent.$refs[
						this.trackedRefName
					][0];
				} else if (this.$parent.$refs[this.trackedRefName].$el) {
					this.trackedRef = this.$parent.$refs[
						this.trackedRefName
					].$el;
				} else {
					this.trackedRef = this.$parent.$refs[this.trackedRefName];
				}
			} else {
				this.trackedRef = this.trackedRefName;
			}

			if (
				this.$parent.$refs[this.popoverRefName] &&
				typeof this.popoverRefName === 'string'
			) {
				this.popperRef =
					this.$parent.$refs[this.popoverRefName].$el ||
					this.$parent.$refs[this.popoverRefName];
			} else {
				this.popperRef = this.popoverRefName;
			}

			if (
				this.$parent.$refs[this.arrowRefName] &&
				typeof this.arrowRefName === 'string'
			) {
				this.arrowRef =
					this.$parent.$refs[this.arrowRefName].$el ||
					this.$parent.$refs[this.arrowRefName];
			} else {
				this.arrowRef = this.arrowRefName;
			}
		},
		updateInstance() {
			this.popperInstance.update();
			this.$nextTick(this.setPopperMinWidth);
		},
		execCreateInstance() {
			if (!this.popperVisible) {
				return;
			}

			if (this.popperInstance) {
				this.popperInstance.destroy();
			}

			const opts = {
				placement: this.placement,
				strategy: this.strategy,
				modifiers: [
					this.currentPositionMod,
					{
						name: 'hide',
						enabled: false
					},
					{
						name: 'flip',
						options: {
							altBoundary: true
						}
					}
				]
			};

			this.sanitazeRefs();

			if (this.arrowRef) {
				opts.modifiers.push({
					name: 'arrow',
					options: {
						element: this.arrowRef
					}
				});
			}

			if (this.modifiers) {
				opts.modifiers.push(...this.modifiers);
			}

			if (this.trackedRef && this.popperRef) {
				this.popperInstance = createPopper(
					this.trackedRef.$el || this.trackedRef,
					this.popperRef.$el || this.popperRef,
					opts
				);
			} else {
				//parent component refs are not rendered
				//console.log('parent component refs are not rendered');
			}
		},
		setPopperMinWidth() {
			if (!this.popperVisible || !this.trackedRef || !this.popperRef) {
				return;
			}

			const reference = this.trackedRef.$el || this.trackedRef;
			const referenceBoundingRect = reference.getBoundingClientRect();

			if (!referenceBoundingRect) return;

			const minWidth = `${referenceBoundingRect.width}px`;

			if (minWidth !== this.popperMinWidth) {
				this.popperMinWidth = minWidth;
				this.$emit('popperMinWidthChanged', minWidth);
			}
		},
		positionModFn({ state }) {
			this.$emit('positionUpdated', state.placement);
		},
		checkIsInVieport(element) {
			if (!element) {
				return true;
			}

			const rect = element.getBoundingClientRect();
			return (
				rect.top + rect.height >= 0 &&
				rect.bottom <=
					(window.innerHeight ||
						document.documentElement.clientHeight)
			);
		},
		checkIsVisibleInAltContainer(element, container) {
			const elementTop = element.getBoundingClientRect().top;
			const elementHeight = element.getBoundingClientRect().height;
			const elementTopWithHeight = elementTop + elementHeight;

			const containerTop = container.getBoundingClientRect().top;
			const containerHeight = container.getBoundingClientRect().height;

			// check if out of viewport
			if (!this.checkIsInVieport(element)) {
				return false;
			}

			// check if out of container
			if (
				elementTopWithHeight > containerTop &&
				elementTop < containerTop + containerHeight
			) {
				return true;
			}

			return false;
		},
		handlePageScrollEvent() {
			const pageScrollUpdate = _.debounce(() => {
				if (this.popperInstance) {
					this.checkIsInVieport(this.trackedRef);
					if (!this.checkIsInVieport(this.trackedRef)) {
						this.popperRef.setAttribute(
							'data-popper-reference-hidden',
							''
						);
					} else {
						this.popperRef.removeAttribute(
							'data-popper-reference-hidden',
							''
						);
					}
				}
			}, 10);

			this.pageScrollEvent = document.addEventListener(
				'scroll',
				pageScrollUpdate
			);
		},
		handleAltBoundary() {
			const inAModal = document.querySelector('#modalBody');
			const inAFlyout = document.querySelector('#flyoutBody');

			if (this.altBoundary || inAModal || inAFlyout) {
				const altBoundary =
					document.querySelector(`#${this.altBoundary}`) ||
					inAModal ||
					inAFlyout;

				const altBoundaryUpdate = _.debounce(() => {
					if (this.popperInstance && this.popperVisible) {
						this.updateInstance();
						// check if target is hidden
						if (
							!this.checkIsVisibleInAltContainer(
								this.$parent.$el,
								altBoundary
							)
						) {
							this.popperRef.setAttribute(
								'data-popper-reference-hidden',
								''
							);
						} else {
							this.popperRef.removeAttribute(
								'data-popper-reference-hidden',
								''
							);
						}
					}
				}, 10);

				if (altBoundary) {
					this.altBoundayScrollEvent = altBoundary.addEventListener(
						'scroll',
						altBoundaryUpdate
					);
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped></style>
